import type { TOC } from '@ember/component/template-only';
import { array } from '@ember/helper';
import { PrismCard, PrismLoadingBar } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { pageTitle } from 'ember-page-title';
import { or } from 'ember-truth-helpers';
import GeneralDatepickersDatepickerWithSidebar from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import GeneralDropdownsDropdownPageSize from 'lumen/components/general/dropdowns/dropdown-page-size';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import GeneralTablesPaginationFooter from 'lumen/components/general/tables/pagination-footer';
import MarketSnapshotsDataPreferences from 'lumen/components/market/snapshots/data-preferences';
import MarketSnapshotsTable from 'lumen/components/market/snapshots/table';
import MarketSnapshotsTableLayout from 'lumen/components/market/snapshots/table-layout';
import type MarketSnapshotsOverviewController from 'lumen/controllers/market/snapshots/overview';
import contextualPageTitle from 'lumen/helpers/contextual-page-title';
import type MarketSnapshotsOverviewRoute from 'lumen/routes/market/snapshots/overview';
import {
  FIRST_HEADER_HEIGHT,
  SUB_HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';
import type { ModelFrom } from 'lumen/utils/routes';

interface MarketSnapshotsOverviewSignature {
  Args: {
    controller: MarketSnapshotsOverviewController;
    model: ModelFrom<MarketSnapshotsOverviewRoute>;
  };
}

const LOADING_BAR_POSITION_TOP = FIRST_HEADER_HEIGHT + SUB_HEADER_HEIGHT;

<template>
  {{pageTitle (contextualPageTitle (t "market.snapshots.view.overview"))}}

  <GeneralPageHeader @isSmall={{true}}>
    <div class="content-wrapper flex items-center">
      {{! @glint-expect-error: Incorrect type }}
      <GeneralDatepickersDatepickerWithSidebar
        @maxDate={{@controller.maxDate}}
        @maxRange="2 years"
        @range={{@controller.dateRange}}
        @rangeChanged={{@controller.updateDateRange}}
        @sidebarLists={{@controller.datepickerSidebarLists}}
      />
    </div>
  </GeneralPageHeader>

  {{#if @controller.model.loadDataItemsTask.last.isRunning}}
    <PrismLoadingBar
      @isFixed={{true}}
      @positionTop={{LOADING_BAR_POSITION_TOP}}
    />
  {{/if}}

  <GeneralPageMain>
    <div class="content-wrapper main-content flex h-full flex-col gap-600">
      <div class="flex items-center gap-200">
        <h2 class="text-neutral-emphasis">{{t
            "market.snapshots.view.overview"
          }}</h2>

        {{#if @controller.totalRecords}}
          <span class="text-300--regular">
            {{t "general.hotel.count" count=@controller.totalRecords}}
          </span>
        {{/if}}

        <MarketSnapshotsDataPreferences
          @dataPreferences={{@controller.dataPreferences}}
          @onSave={{@controller.updateDataPreferences}}
          class="ms-auto"
        />

        <MarketSnapshotsTableLayout
          @onSave={{@controller.updateTableLayoutPreferences}}
          @tableLayoutPreferences={{@controller.tableLayoutPreferences}}
        />
      </div>

      <PrismCard @contentLayout="noPadding" class="overflow-hidden">
        <:content>
          <MarketSnapshotsTable
            @dataItems={{or
              @controller.model.loadDataItemsTask.last.value.items
              (array)
            }}
            @groupColumnsBy={{@controller.tableLayoutPreferences.groupColumnsBy}}
            @isLoading={{@controller.model.loadDataItemsTask.last.isRunning}}
            @onSort={{@controller.onSort}}
            @orderBy={{@controller.orderBy}}
            @totals={{@controller.model.loadDataItemsTask.last.value.meta.totals}}
            @varianceType={{@controller.dataPreferences.variance}}
            @visibleMetricTypes={{@controller.visibleMetricTypes}}
          />
        </:content>

        <:footer>
          {{#if
            @controller.model.loadDataItemsTask.lastSuccessful.value.meta.count
          }}
            <GeneralTablesPaginationFooter
              @currentPage={{@controller.page}}
              @lastPage={{@controller.model.loadDataItemsTask.lastSuccessful.value.meta.page_count}}
              @onChangePageSize={{@controller.onChangePageSize}}
              @onSetPage={{@controller.setPage}}
              @pageSizeDropdown={{GeneralDropdownsDropdownPageSize}}
              @pageSizeOptions={{@controller.pageSizeOptions}}
              @pageSizeValue={{@controller.currentPageSizeOption}}
              @showingText={{@controller.showingText}}
            />
          {{/if}}
        </:footer>
      </PrismCard>
    </div>
  </GeneralPageMain>
</template> satisfies TOC<MarketSnapshotsOverviewSignature>;
